
import { reactive, onMounted, watch, inject, computed } from "vue";
import ResearchOrganizationRepositoryAPI from "@/modules/research-organization/research-organization-repository-api";
import ResearchCategorySelectionData from "@/modules/research-category-selection-data";
import ResearchEquipmentCreationDto, {
  EquipmentComponent,
  EquipmentPriceList,
  EquipmentUtilizationList,
} from "@/modules/research-equipment/research-equipment-creation-dto";

import ResearchEquipmentRepositoryAPI from "@/modules/research-equipment/research-equipment-repository-api";

import axios from "@/services/axios";
import { getCategoryLanguageName } from "@/locales/i18n";
import { useI18n } from "vue-i18n";
import { location } from "@/utils/default-data";
import UserRepositoryAPI from "@/modules/user/user-repository-api";
import ResearchInfrastructureRepositoryAPI from "@/modules/research-infrastructure/research-infrastructure-repository-api";

export default {
  props: {
    editMode: Boolean,
  },
  setup(props: any) {
    const { t } = useI18n();
    const toast: any = inject("toast");
    const isEditModeEnabled = props.editMode as boolean;

    const localProperty = getCategoryLanguageName();

    const state = reactive({
      localProperty: localProperty,
      users: [],
      documentUploadDialog: false,
      documentUploadDialogFile: false,
      confirmationDialog: false,
      confirmationDialogFile: false,
      selectedBookingDocumentIndex: -1,
      selectedEquipmentImageItem: {} as any,
      selectedEquipmentFileItem: {} as any,
      selectedResearchOrganization: {},
      equipmentDto: {
        components: [],
        pricelist: [],
        utilizationlist: [],
        images: [],
        files: [],
        latitude: location.lat,
        longitude: location.lng,
        street: location.address,
        postalCode: location.postalCode,
        department: location.department,
      } as ResearchEquipmentCreationDto,
      filteredUniLeedOrderType: [],
      filteredUniLeedType: [],
      filteredUsers: [],
      equipmentImages: [],
      responsiveOptions: [
        {
          breakpoint: "1024px",
          numVisible: 3,
          numScroll: 3,
        },
        {
          breakpoint: "600px",
          numVisible: 2,
          numScroll: 2,
        },
        {
          breakpoint: "480px",
          numVisible: 1,
          numScroll: 1,
        },
      ],
    });

    const { researchInfrastructureState, loadResearchInfrastructureGroup } =
      ResearchInfrastructureRepositoryAPI();
    const { researchOrganizationState } = ResearchOrganizationRepositoryAPI();
    const { getSelectedResearchEquipment } = ResearchEquipmentRepositoryAPI();

    const {
      getUser,
      hasUniversityAccessRole,
      hasBriumAccessRole,
      hasFacultyAccessRole,
    } = UserRepositoryAPI();

    const {
      loadEquipmentCategories,
      getResearchCategories,
      loadEquipmentTargetGroup,
    } = ResearchCategorySelectionData();

    onMounted(() => {
      loadUsers();
      loadResearchInfrastructureGroup();
      loadEquipmentTargetGroup();
      loadEquipmentCategories();
      state.equipmentDto.researchOrganizationId =
        getUser.value.researchOrganizationId;
    });

    watch(
      () => state.equipmentDto.researchOrganizationId,
      (newValue) => {
        state.filteredUsers = state.users.filter(
          (item: any) => item.researchOrganizationId === newValue
        );
      }
    );

    watch(
      () => state.equipmentDto.researchInfrastructureId,
      () => {
        const researchOrganizationId =
          state.equipmentDto.researchOrganizationId;
        state.filteredUsers = state.users.filter(
          (item: any) => item.researchOrganizationId === researchOrganizationId
        );
      }
    );

    watch(
      () => state.equipmentDto.equipmentUniLeedClassTypeId,
      (newValue) => {
        state.filteredUniLeedType = [];
        state.filteredUniLeedOrderType = getResearchCategories.uniLeedClassTypes
          .filter((item: any) => item.id === newValue)
          .map((item: any) => item.children)[0];
      }
    );

    watch(
      () => state.equipmentDto.equipmentUniLeedOrderTypeId,
      (newValue) => {
        if (!state.filteredUniLeedOrderType) {
          return;
        }
        state.filteredUniLeedType = state.filteredUniLeedOrderType
          .filter((item: any) => item.id === newValue)
          .map((item: any) => item.children)[0];
      }
    );

    watch(
      () => getSelectedResearchEquipment.value,
      (newValue) => {
        state.equipmentDto.latitude = location.lat;
        state.equipmentDto.longitude = location.lng;
        if (false === isEditModeEnabled) {
          return;
        }
        state.equipmentDto.researchInfrastructureId =
          newValue.researchInfrastructureId;
        state.equipmentDto.researchGroupId = newValue.researchGroup?.id;
        state.equipmentDto.name = newValue.name;
        state.equipmentDto.nameEnglish = newValue.nameEnglish;
        state.equipmentDto.serialNumber = newValue.serialNumber;
        state.equipmentDto.number = newValue.number;
        state.equipmentDto.webURL = newValue.webURL;
        state.equipmentDto.purchasePrice = newValue.purchasePrice;
        state.equipmentDto.purchaseEquipmentDateYear =
          newValue.purchaseEquipmentDateYear;

        state.equipmentDto.researchEquipmentId = newValue.id;
        state.equipmentDto.researchOrganizationId =
          newValue.researchOrganization.id;
        state.equipmentDto.userIds = newValue.contactUsers?.map(
          (item: any) => item.id
        );

        state.equipmentDto.components = newValue.components;
        state.equipmentDto.pricelist = newValue.researchEquipmentPrice;
        state.equipmentDto.utilizationlist =
          newValue.researchEquipmentUtilization;
        state.equipmentDto.equipmentAccessMethodId =
          newValue.equipmentAccessMethod?.id;

        state.equipmentDto.equipmentAccessPurposeId =
          newValue.equipmentAccessPurpose?.id;
        state.equipmentDto.purposeTypeDescription =
          newValue.accessPurposeDescription;
        state.equipmentDto.purposeTypeDescriptionEnglish =
          newValue.accessPurposeDescriptionEnglish;

        state.equipmentDto.equipmentAccessTypeId =
          newValue.equipmentAccessType?.id;
        state.equipmentDto.accessTypeDescription =
          newValue.accessTypeDescription;
        state.equipmentDto.accessTypeDescriptionEnglish =
          newValue.accessTypeDescriptionEnglish;

        state.equipmentDto.equipmentUniLeedClassTypeId =
          newValue.equipmentUniLeedClassType?.id;
        state.equipmentDto.equipmentUniLeedOrderTypeId =
          newValue.equipmentUniLeedOrderType?.id;
        state.equipmentDto.equipmentUniLeedTypeId =
          newValue.equipmentUniLeedType?.id;
        state.equipmentDto.equipmentLifeCycleStatusId =
          newValue.equipmentLifeCycleStatus?.id;
        state.equipmentDto.equipmentCriteriaId = newValue.equipmentCriteria?.id;

        state.equipmentDto.cityId = newValue.location.cityId;
        state.equipmentDto.street = newValue.location.street;
        state.equipmentDto.postalCode = newValue.location.postalCode;
        state.equipmentDto.latitude = newValue.location.latitude;
        state.equipmentDto.longitude = newValue.location.longitude;
        state.equipmentDto.department = newValue.location.departments?.map(
          (item: any) => item.name
        )[0];
        state.equipmentDto.targetGroupIds = newValue.equipmentTargetGroups?.map(
          (item: any) => item.id
        );
        state.equipmentDto.fundingTypeIds = newValue.equipmentFundingTypes?.map(
          (item: any) => item.id
        );

        state.equipmentDto.equipmentResearchOrientationTypeIds =
          newValue.equipmentResearchOrientationTypes?.map(
            (item: any) => item.id
          );
        state.equipmentDto.technicalCharacteristic =
          newValue.technicalCharacteristic;

        state.equipmentDto.images = newValue.images;

        state.equipmentDto.costUnit = newValue.costUnit;
        state.equipmentDto.costObject = newValue.costObject;
        state.equipmentDto.protectionAgainstIonizingRadiation =
          newValue.protectionAgainstIonizingRadiation;
        state.equipmentDto.measuresAgainstIonizingRadiation =
          newValue.measuresAgainstIonizingRadiation;
        state.equipmentDto.amortizationPeriod = newValue.amortizationPeriod;
        state.equipmentDto.publicAnnouncement = newValue.publicAnnouncement;
        state.equipmentDto.internalUseOnly = newValue.internalUseOnly;
        state.equipmentDto.serialNumberPurchase = newValue.serialNumberPurchase;

        state.equipmentDto.amortizationCost = newValue.amortizationCost;
        state.equipmentDto.materialsAndServicesCost =
          newValue.materialsAndServicesCost;
        state.equipmentDto.workCost = newValue.workCost;
        state.equipmentDto.sumMaintenanceCost = newValue.sumMaintenanceCost;

        loadImages();
        loadFiles();
      }
    );

    const onResearchEquipmentCreateClick = () => {
      if (isEditModeEnabled) {
        // Update equipment data
        axios
          .put("/api/v1/ResearchEquipment", state.equipmentDto)
          .then((response) => {
            if (response.status === 204) {
              const summaryText = `${state.equipmentDto.name} posodobljena`;
              notificationDisplay(
                "success",
                `Raziskovalna oprema`,
                summaryText
              );
            }
          })
          .catch((error) => {
            const response = error.response;
            notificationDisplay(
              "warn",
              `Raziskovalna oprema`,
              `${response.statusText}: ${response.data.message}`
            );
          });

        if (state.equipmentDto.images.length > 0) {
          // Upload equipment images
          let formData = new FormData();
          state.equipmentDto.images.forEach((file) => {
            formData.append("Images", file);
          });
          axios
            .put(
              `/api/v1/ResearchEquipment/${state.equipmentDto.researchEquipmentId}/imageUpload`,
              formData
            )
            .then((response) => {
              if (response.status === 204) {
                loadImages();
                const summaryText = `Images was uploaded`;
                notificationDisplay(
                  "success",
                  `Raziskovalna oprema`,
                  summaryText
                );
              }
            })
            .catch((error) => {
              const response = error.response;
              notificationDisplay(
                "warn",
                `Raziskovalna oprema`,
                `${response.statusText}: ${response.data.message}`
              );
            });
        }

        if (state.equipmentDto.files.length > 0) {
          // Upload equipment files
          let formDataFileUpload = new FormData();
          state.equipmentDto.files.forEach((file) => {
            formDataFileUpload.append("Files", file);
          });
          axios
            .put(
              `/api/v1/ResearchEquipment/${state.equipmentDto.researchEquipmentId}/fileUpload`,
              formDataFileUpload
            )
            .then((response) => {
              if (response.status === 204) {
                loadFiles();
                const summaryText = `File was uploaded`;
                notificationDisplay(
                  "success",
                  `Raziskovalna oprema`,
                  summaryText
                );
              }
            })
            .catch((error) => {
              const response = error.response;
              notificationDisplay(
                "warn",
                `Raziskovalna oprema`,
                `${response.statusText}: ${response.data.message}`
              );
            });
        }

        return;
      }

      // Add new equipment data
      axios
        .post("/api/v1/ResearchEquipment", state.equipmentDto)
        .then((response: any) => {
          if (response.status === 201) {
            const summaryText = `${response.data.name} with number ${response.data.number} was added`;
            notificationDisplay("success", `Research Equipment`, summaryText);
          }
        })
        .catch((error) => {
          const response = error.response;
          notificationDisplay(
            "warn",
            `Research Equipment failed`,
            `${response.statusText}: ${response.data.message}`
          );
        });
    };

    function loadFiles() {
      axios
        .get(
          `api/v1/ResearchEquipment/${state.equipmentDto.researchEquipmentId}/files`
        )
        .then((response: any) => {
          state.equipmentDto.files = response.data;
        });
    }
    function loadImages() {
      axios
        .get(
          `api/v1/ResearchEquipment/${state.equipmentDto.researchEquipmentId}/images`
        )
        .then((response: any) => {
          state.equipmentImages = response.data;
        });
    }

    const onEquipmentPriceItemAddClick = (): void => {
      let componentItem = {
        dateTo: "",
        dateFrom: "",
        userExpertise: "",
      } as EquipmentPriceList;
      state.equipmentDto.pricelist.push(componentItem);
    };

    const onEquipmentPriceItemRemoveClick = (index: number): void => {
      state.equipmentDto.pricelist.splice(index, 1);
    };

    const onEquipmentUtilizationItemAddClick = (): void => {
      const currentYear = new Date().getFullYear();
      let item = {
        year: currentYear,
        annualUtilizationRatePercent: 0,
        depreciationRatePercentage: 0,
      } as EquipmentUtilizationList;
      state.equipmentDto.utilizationlist.push(item);
    };

    const onEquipmentUtilizationItemRemoveClick = (index: number): void => {
      state.equipmentDto.utilizationlist.splice(index, 1);
    };

    const onEquipmentComponentAddClick = (): void => {
      let componentItem = {
        name: "",
        description: "",
      } as EquipmentComponent;
      state.equipmentDto.components.push(componentItem);
    };

    const onEquipmentComponentRemoveClick = (index: number): void => {
      state.equipmentDto.components.splice(index, 1);
    };

    const onPreviewEquipmentImageUploadClick = (event: any): void => {
      event.files.forEach((file: any) => {
        file.url = file.name;
        state.equipmentDto.images.push(file);
      });
      state.documentUploadDialog = false;
    };

    const onPreviewEquipmentFileUploadClick = (event: any): void => {
      event.files.forEach((file: any) => {
        state.equipmentDto.files.push(file);
      });
      state.documentUploadDialogFile = false;
    };

    const onRemoveEquipmentImageClickDialog = (
      index: number,
      item: any
    ): void => {
      state.selectedBookingDocumentIndex = index;
      state.selectedEquipmentImageItem = item;
      state.confirmationDialog = true;
    };

    const onRemoveEquipmentFileClickDialog = (
      index: number,
      item: any
    ): void => {
      state.selectedBookingDocumentIndex = index;
      state.selectedEquipmentFileItem = item;
      state.confirmationDialogFile = true;
    };

    const onRemoveEquipmentImageClick = (): void => {
      state.confirmationDialog = false;
      axios
        .delete(
          `api/v1/ResearchEquipment/${state.equipmentDto.researchEquipmentId}/images/${state.selectedEquipmentImageItem.id}`
        )
        .then(() => {
          state.equipmentDto.images.splice(
            state.selectedBookingDocumentIndex,
            1
          );
        })
        .catch((error) => console.error(error));
    };

    const onRemoveEquipmentFileClick = (): void => {
      state.confirmationDialogFile = false;
      axios
        .delete(
          `api/v1/ResearchEquipment/${state.equipmentDto.researchEquipmentId}/files/${state.selectedEquipmentFileItem.id}`
        )
        .then(() => {
          state.equipmentDto.files.splice(
            state.selectedBookingDocumentIndex,
            1
          );
        })
        .catch((error) => console.error(error));
    };

    const getFileName = (url: string) => {
      return url.split("/").pop();
    };

    async function loadUsers() {
      const response = await axios.get("api/v1/User?PageNumber=0&PageSize=500");
      state.users = response.data;
    }

    const notificationDisplay = (
      severity: string,
      summary: string,
      detail: string
    ): void => {
      toast.add({
        severity: severity,
        summary: summary,
        detail: detail,
        life: 3000,
      });
    };

    return {
      t,
      state: state,
      researchOrganizationState: researchOrganizationState,
      researchInfrastructureState: researchInfrastructureState,
      getResearchCategories: getResearchCategories,
      onResearchEquipmentCreateClick,
      onEquipmentComponentAddClick,
      onEquipmentComponentRemoveClick,
      onEquipmentPriceItemAddClick,
      onEquipmentPriceItemRemoveClick,
      onEquipmentUtilizationItemAddClick,
      onEquipmentUtilizationItemRemoveClick,
      getSelectedResearchEquipment,
      getEditMode: computed(() => isEditModeEnabled),
      onPreviewEquipmentImageUploadClick,
      onPreviewEquipmentFileUploadClick,
      onRemoveEquipmentImageClickDialog,
      onRemoveEquipmentFileClickDialog,
      onRemoveEquipmentImageClick,
      onRemoveEquipmentFileClick,
      locationData: computed(() => {
        return {
          lat: state.equipmentDto.latitude || location.lat,
          lng: state.equipmentDto.longitude || location.lng,
        } as any;
      }),
      getFileName,
      organizationDisabled: computed(() => {
        return (
          !hasBriumAccessRole() &&
          !hasUniversityAccessRole() &&
          !hasFacultyAccessRole()
        );
      }),
    };
  },
};
